import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotifications } from "../hooks/useNotifications";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal/index";
import { Bell, CheckCheck } from "lucide-react";
import { Button, cx, Icon } from "@/shared/rd";
import type { NotificationBellProps, PanelPosition } from "../types/notification-bell.types";

function formatRelativeTime(date: Date, locale: string): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMinutes < 1) {
    return locale === "ar" ? "الآن" : "Just now";
  }
  if (diffMinutes < 60) {
    return locale === "ar" ? `منذ ${diffMinutes} د` : `${diffMinutes}m ago`;
  }
  if (diffHours < 24) {
    return locale === "ar" ? `منذ ${diffHours} س` : `${diffHours}h ago`;
  }
  if (diffDays < 7) {
    return locale === "ar" ? `منذ ${diffDays} ي` : `${diffDays}d ago`;
  }
  return date.toLocaleDateString(locale, { month: "short", day: "numeric" });
}

export function NotificationBell({ adminId, locale }: NotificationBellProps) {
  const t = useTranslations();
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead, markAllAsRead } =
    useNotifications(adminId);
  const { pathname } = useLocation();
  const [openOn, setOpenOn] = useState<string | null>(null);
  // Forget the opening route so returning to it cannot reopen the panel.
  if (openOn !== null && openOn !== pathname) setOpenOn(null);
  const isOpen = openOn === pathname;
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<PanelPosition>({ top: 0, left: 0 });
  const isRtl = locale === "ar";
  // Clamp to the viewport so the panel never overflows on <=336px screens.
  const panelWidth = Math.min(
    320,
    (typeof window !== "undefined" ? window.innerWidth : 320) - 16,
  );

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const top = rect.bottom + 8;
    const left = isRtl
      ? Math.min(rect.right, window.innerWidth - 8) - panelWidth
      : Math.max(8, rect.left);
    setPosition({ top, left: Math.max(8, Math.min(left, window.innerWidth - panelWidth - 8)) });
  }, [isRtl, panelWidth]);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();

    function handleClickOutside(event: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setOpenOn(null);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpenOn(null);
      }
    }

    window.addEventListener("resize", updatePosition);
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, updatePosition]);

  const openNotification = (notification: (typeof notifications)[number]) => {
    void markAsRead(notification.id);
    setOpenOn(null);
    const storeId = notification.data?.storeId;
    const orderId = notification.data?.orderId;
    const paymentId = notification.data?.paymentId;
    // A wallet top-up request is reviewed in the queue, not on the store page:
    // the notification carries the ids the queue deep-links the row with.
    if (paymentId) {
      const query = new URLSearchParams({ paymentId });
      if (storeId) {
        query.set("storeId", storeId);
      }
      navigate(`/${locale}/admin/wallet-top-up-requests?${query.toString()}`);
    } else if (storeId && orderId) {
      navigate(`/${locale}/admin/stores/${storeId}/orders/${orderId}`);
    } else if (storeId) {
      navigate(`/${locale}/admin/stores/${storeId}`);
    }
  };

  return (
    <>
      <span className={cx("rd-bell", isOpen && "rd-bell-open")}>
        <Button
          ref={buttonRef}
          variant="ghost"
          iconOnly
          icon={Bell}
          title={t(keys.admin.notificationsTitle)}
          aria-expanded={isOpen}
          onClick={() => setOpenOn((prev) => (prev === pathname ? null : pathname))}
        />
        {unreadCount > 0 ? <span className="rd-bell-count">{unreadCount > 99 ? "99+" : unreadCount}</span> : null}
      </span>

      {isOpen
        ? createPortal(
            <div
              ref={panelRef}
              dir={isRtl ? "rtl" : "ltr"}
              className="rd-root rd-notif"
              role="dialog"
              aria-label={t(keys.admin.notificationsTitle)}
              style={{ position: "fixed", zIndex: 9999, top: position.top, left: position.left, width: panelWidth }}
            >
              <div className="rd-notif-head">
                <h3 className="rd-notif-title" style={{ margin: 0 }}>
                  {t(keys.admin.notificationsTitle)}
                </h3>
                <div className="rd-spacer" />
                {unreadCount > 0 ? (
                  <button type="button" className="rd-notif-mark" onClick={() => void markAllAsRead()}>
                    <Icon icon={CheckCheck} size={15} />
                    <span>{t(keys.admin.notificationsMarkAllRead)}</span>
                  </button>
                ) : null}
              </div>
              <div className="rd-notif-list">
                {notifications.length === 0 ? (
                  <div className="rd-notif-empty">{t(keys.admin.notificationsEmpty)}</div>
                ) : (
                  notifications.map((notification) => (
                    <button
                      key={notification.id}
                      type="button"
                      onClick={() => openNotification(notification)}
                      className={cx("rd-notif-item", !notification.read && "rd-unread")}
                    >
                      <span className="rd-notif-text">
                        <span className="rd-notif-line">
                          <b dir="auto">{notification.title}</b>
                          <span className="rd-notif-time">
                            {notification.createdAt ? formatRelativeTime(notification.createdAt.toDate(), locale) : ""}
                          </span>
                        </span>
                        <span className="rd-notif-body" dir="auto">
                          {notification.body}
                        </span>
                      </span>
                      <span className="rd-notif-dot" />
                    </button>
                  ))
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
